import { ConversionCancelledError, ffmpegClient } from '@/services/ffmpegClient';
import { buildCropDetectArgs, buildGifPlan } from '@/services/gifPlan';
import { useProjectStore } from '@/store/projectStore';
import { toast } from '@/store/toastStore';
import { formatBytes } from '@/utils/format';
import { safeInputExtension } from '@/utils/sanitize';

// Progress weighting across the two passes. The palette pass decodes the same
// frames but skips encoding, so it's the cheaper ~40%.
const ENGINE_SHARE = 0.04;
const PALETTE_SHARE = 0.36;
const ENCODE_SHARE = 0.6;

const PROGRESS_THROTTLE_MS = 150;
const ETA_SMOOTHING = 0.3;
const MAX_ERROR_LENGTH = 600;

/**
 * Run the full two-pass conversion for the current project state.
 * Progress is throttled into the store; the ETA is an exponential moving
 * average so it doesn't jitter with per-chunk timing noise.
 */
export async function startConversion(): Promise<void> {
  const state = useProjectStore.getState();
  const { file, meta, trim, crop, settings, conversion } = state;
  if (!file || !meta) return;
  if (
    conversion.phase === 'loading-engine' ||
    conversion.phase === 'palette' ||
    conversion.phase === 'encoding'
  ) {
    return; // already running
  }

  const plan = buildGifPlan(meta, trim, crop, settings);
  for (const warning of plan.warnings) toast.warning('Heads up', warning);

  const inputName = `input${safeInputExtension(meta.type, meta.name)}`;
  state.conversionStarted();

  const startedAt = performance.now();
  let smoothedEta: number | null = null;
  let lastPush = 0;

  const onProgress = (phase: 'palette' | 'encode', ratio: number) => {
    const overall =
      phase === 'palette'
        ? ENGINE_SHARE + ratio * PALETTE_SHARE
        : ENGINE_SHARE + PALETTE_SHARE + ratio * ENCODE_SHARE;

    const now = performance.now();
    if (now - lastPush < PROGRESS_THROTTLE_MS && overall < 0.999) return;
    lastPush = now;

    let eta: number | null = null;
    if (overall > 0.05) {
      const elapsed = (now - startedAt) / 1000;
      const raw = (elapsed * (1 - overall)) / overall;
      smoothedEta =
        smoothedEta === null ? raw : smoothedEta * (1 - ETA_SMOOTHING) + raw * ETA_SMOOTHING;
      eta = smoothedEta;
    }

    useProjectStore
      .getState()
      .conversionProgress(overall, eta, phase === 'palette' ? 'palette' : 'encoding');
  };

  try {
    const blob = await ffmpegClient.convert({
      file,
      inputName,
      paletteArgs: plan.paletteArgs,
      encodeArgs: plan.encodeArgs,
      outputDuration: plan.outputDuration,
      onProgress,
    });

    useProjectStore.getState().conversionDone({
      blob,
      size: blob.size,
      width: plan.width,
      height: plan.height,
      fps: plan.fps,
      durationSec: plan.outputDuration,
    });
    toast.success('GIF ready', `${formatBytes(blob.size)} — preview below.`);
  } catch (error) {
    if (error instanceof ConversionCancelledError) {
      useProjectStore.getState().conversionCancelled();
      return;
    }
    const message = (error instanceof Error ? error.message : String(error)).slice(
      0,
      MAX_ERROR_LENGTH,
    );
    useProjectStore.getState().conversionFailed(message);
    toast.error('Conversion failed', 'Details are shown in the convert panel.');
  }
}

/** Hard cancel: terminates the worker; the rejected promise settles the store. */
export function cancelConversion(): void {
  useProjectStore.getState().conversionCancelled();
  ffmpegClient.cancelActive();
}

export function retryConversion(): Promise<void> {
  return startConversion();
}

/**
 * Detect black bars over a short sample of the trimmed range and apply the
 * result as the crop. Returns true when a crop was applied.
 */
export async function autoDetectCrop(): Promise<boolean> {
  const { file, meta, trim } = useProjectStore.getState();
  if (!file || !meta) return false;

  const inputName = `input${safeInputExtension(meta.type, meta.name)}`;
  try {
    const crop = await ffmpegClient.detectCrop({
      file,
      inputName,
      args: buildCropDetectArgs(meta, trim),
      sourceWidth: meta.width,
      sourceHeight: meta.height,
    });
    if (crop) {
      useProjectStore.getState().setCrop(crop);
      toast.success('Cropped to picture', `Black bars trimmed to ${crop.width}×${crop.height}.`);
      return true;
    }
    toast.info('No black bars found', 'The full frame already looks like picture.');
    return false;
  } catch (error) {
    if (error instanceof ConversionCancelledError) return false;
    toast.error('Detection failed', error instanceof Error ? error.message : String(error));
    return false;
  }
}
